import React, { useEffect, useMemo, useRef, useState } from "react";
import "../landing/landing.css";
import "./JoinLobby.css";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { updateGameSettings } from "../../functions/updateGameSettings";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from '../uiSlice';
import Swal from "sweetalert2";
import { validateHuntPin } from "../../functions/validateHuntPin";
import { autoJoinHunt } from "../../functions/autoJoinHunt";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { s3PublicUrl } from "../../utils/s3PublicUrl";

/** The hunt's lobby (pin/link/rounds) is fully configured by the admin in Studio — this page's
 * only job is to gate entry until the player enters that admin-generated PIN correctly.
 * Background/colors come from the admin's theme, same as the login page — nothing static. */
const JoinLobby = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((s) => s.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const { data } = useSelector((state) => state.theme);

  const headingColor = data?.headingColor || "#D83307";
  const textColor = data?.textColor || "#5B5B5B";
  const buttonBg = data?.buttonBgColor || "#D83307";
  const buttonText = data?.buttonTextColor || "#FFFFFF";
  const rawCover = data?.background_mob || data?.hunt_cover_image || data?.background_desk;
  const coverImage = rawCover ? s3PublicUrl(rawCover) : "";

  useEffect(() => {
    document.body.classList.add("login-static-bg");
    if (coverImage) {
      document.body.style.backgroundImage = `url("${encodeURI(coverImage)}")`;
    }
    return () => {
      document.body.classList.remove("login-static-bg");
      document.body.style.backgroundImage = "";
    };
  }, [coverImage]);

  // Redux's `auth.user` only exists after loginUser() ran in this SPA session — a reload or a
  // direct navigation here loses it even though the login itself is still valid. sessionStorage's
  // `userData` (persisted by loginSlice) survives that, so fall back to it (same pattern as
  // ManagerDashboard.jsx) instead of failing with "Missing userId".
  const storedUserData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [user, dispatch]);

  // These APIs require `Authorization: Bearer ${sessionId}&${organizationId}`.
  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = user?.sessionId ?? user?.sessionid ?? storedUserData.sessionId ?? "";
    const oid = user?.organizationId ?? user?.orgId ?? user?.organizationid ?? storedUserData.organizationId ?? "";
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, user, storedUserData]);

  // A link built by buildJoinLink() (Teams.jsx / printed QR) carries the lobby PIN in a `lobby`
  // param (that's the one the ExtraMile OTP gateway actually forwards — see buildJoinLink) —
  // `lobbyPin` is kept only as a fallback for an older link still floating around. Normally
  // loginSlice.js already auto-joins from this before the player even reaches this page; this is
  // just a safety net for whatever landed here without going through that.
  const lobbyPinFromUrl = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("lobby") || params.get("lobbyPin");
    return raw ? raw.replace(/[^\d]/g, "").slice(0, 6) : "";
  }, []);

  // The external ExtraMile OTP gateway resolves sessionId/organizationId during login but doesn't
  // always carry a custom param like lobbyPin through its own redirect back into this app — so a
  // QR/link join can land here with the PIN dropped even though the session is already fully
  // resolved. sessionId+organizationId alone is exactly what login.jsx's own "arrived via the
  // admin's join link" bypass already trusts to skip straight to auto-join (see arrivedViaJoinLink
  // there) — apply that same trust here instead of forcing a PIN re-entry for something the login
  // step already proved.
  const arrivedViaJoinLink = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return !!(params.get("sessionId") && params.get("organizationId"));
  }, []);

  const [joinPin, setJoinPin] = useState(lobbyPinFromUrl);
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState("");
  const [autoJoinFailed, setAutoJoinFailed] = useState(false);
  const stageIdRef = useRef(null);
  const autoJoinedRef = useRef(false);

  const wrongPinPopup = async () => {
    await Swal.fire({
      icon: "error",
      title: "Wrong Lobby PIN",
      text: "Please check the PIN and try again.",
      confirmButtonText: "OK",
      confirmButtonColor: buttonBg,
    });
  };

  const joinWithPin = async (pinValue) => {
    const pin = String(pinValue || "").trim();
    if (!pin) {
      await Swal.fire({
        icon: "warning",
        title: "Enter Lobby PIN",
        text: "Ask your host for the lobby PIN.",
        confirmButtonColor: buttonBg,
      });
      return;
    }
    if (!tokenForApi) return;
    setJoinError("");
    setJoinLoading(true);
    try {
      const resp = await validateHuntPin({ token: tokenForApi, lobby_pin: pin });
      if (!resp?.match) {
        setAutoJoinFailed(true);
        await wrongPinPopup();
        return;
      }
      const uid =
        user?.userId ?? user?.userid ?? user?.id ?? storedUserData.userId ?? storedUserData.userid ?? storedUserData.id;
      if (!uid) throw new Error("Missing userId");

      // Seed an external report row the moment this player joins the hunt, same as the
      // join-via-link path — non-blocking, join proceeds even if this call fails.
      let reportId = user?.reportId ?? user?.reportid ?? storedUserData.reportId ?? null;
      if (!reportId) {
        try {
          const seeded = await sendReport({
            sessionId: user?.sessionId ?? storedUserData.sessionId,
            organizationId: user?.organizationId ?? storedUserData.organizationId,
            role: "GUEST_USER",
            token: "",
            name: user?.fullName ?? user?.name ?? user?.username ?? storedUserData.name ?? "",
            userId: uid,
            gameId: process.env.REACT_APP_EXTRAMILE_GAME_ID || "",
            points: 0,
            time: "00:00",
          });
          reportId = extractReportIdFromResponse(seeded, uid);
        } catch (e) {
          console.warn("[JoinLobby] sendReport insert failed:", e?.message || e);
        }
      }

      const upd = await updateGameSettings({
        token: tokenForApi,
        userId: uid,
        ...(stageIdRef.current ? { stageId: stageIdRef.current } : {}),
        themeName: resp.themeName ?? "theme0",
        lobby_pin: resp.lobby_pin ?? pin,
        lobby_link: resp.lobby_link ?? null,
        show_question: true,
        email: user?.email ?? storedUserData.email ?? null,
        fullName: user?.fullName ?? user?.name ?? user?.username ?? storedUserData.name ?? null,
        employeeId: user?.employeeId ?? user?.employeeid ?? storedUserData.employeeId ?? null,
        user_type: user?.role ?? user?.source ?? storedUserData.role ?? "GUEST_USER",
        reportid: reportId,
      });
      if (upd?.stageId) stageIdRef.current = String(upd.stageId);

      const gameSettings = {
        reportId,
        lobby_pin: resp.lobby_pin ?? pin,
        lobby_link: resp.lobby_link ?? null,
        stageId: stageIdRef.current ?? null,
        themeName: resp.themeName ?? "theme0",
      };
      sessionStorage.setItem("gameSettings", JSON.stringify(gameSettings));

      navigate("/team-registration", {
        replace: true,
        state: {
          stageId: stageIdRef.current,
          themeName: resp.themeName ?? "theme0",
        },
      });
    } catch (err) {
      setAutoJoinFailed(true);
      setJoinError(err?.message || "Failed to join lobby");
      await Swal.fire("Error", err?.message || "Failed to join lobby", "error");
    } finally {
      setJoinLoading(false);
    }
  };

  const handleJoinLobby = async (e) => {
    e.preventDefault();
    await joinWithPin(joinPin);
  };

  // Same auto-join login.jsx's handlePlay() does for arrivedViaJoinLink — resolves the org's
  // currently-published hunt directly from the session token, no PIN needed at all.
  const joinViaLink = async () => {
    setJoinError("");
    setJoinLoading(true);
    try {
      const uid = user?.userId ?? user?.userid ?? user?.id ?? storedUserData.userId ?? storedUserData.userid ?? storedUserData.id;
      if (!uid) throw new Error("Missing userId");

      let reportId = user?.reportId ?? user?.reportid ?? storedUserData.reportId ?? null;
      if (!reportId) {
        try {
          const seeded = await sendReport({
            sessionId: user?.sessionId ?? storedUserData.sessionId,
            organizationId: user?.organizationId ?? storedUserData.organizationId,
            role: "GUEST_USER",
            token: "",
            name: user?.fullName ?? user?.name ?? user?.username ?? storedUserData.name ?? "",
            userId: uid,
            gameId: process.env.REACT_APP_EXTRAMILE_GAME_ID || "",
            points: 0,
            time: "00:00",
          });
          reportId = extractReportIdFromResponse(seeded, uid);
        } catch (e) {
          console.warn("[JoinLobby] sendReport insert failed:", e?.message || e);
        }
      }

      const resp = await autoJoinHunt({
        token: tokenForApi,
        userId: uid,
        email: user?.email ?? storedUserData.email,
        fullName: user?.fullName ?? user?.name ?? user?.username ?? storedUserData.name,
        employeeId: user?.employeeId ?? user?.employeeid ?? storedUserData.employeeId,
        user_type: user?.role ?? user?.source ?? storedUserData.role ?? "GUEST_USER",
        reportid: reportId,
      });
      if (!resp?.success) {
        setAutoJoinFailed(true);
        return;
      }
      sessionStorage.setItem(
        "gameSettings",
        JSON.stringify({
          stageId: resp.stageId,
          themeName: resp.themeName,
          lobby_pin: resp.lobby_pin,
          lobby_link: resp.lobby_link,
          reportId,
        })
      );
      navigate("/team-registration", { replace: true, state: { stageId: resp.stageId, themeName: resp.themeName } });
    } catch (err) {
      setAutoJoinFailed(true);
      setJoinError(err?.message || "Failed to join lobby");
    } finally {
      setJoinLoading(false);
    }
  };

  // A lobbyPin arriving in the URL means the player already has it (printed material, a shared
  // link) — skip the manual entry screen entirely and go straight to Team Registration.
  useEffect(() => {
    if (autoJoinedRef.current) return;
    if (!tokenForApi) return;
    if (lobbyPinFromUrl) {
      autoJoinedRef.current = true;
      joinWithPin(lobbyPinFromUrl);
      return;
    }
    // No lobbyPin survived the OTP redirect, but sessionId/organizationId did — that's already
    // proof enough this came from the admin's own link (see arrivedViaJoinLink above).
    if (arrivedViaJoinLink) {
      autoJoinedRef.current = true;
      joinViaLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobbyPinFromUrl, arrivedViaJoinLink, tokenForApi]);

  if ((lobbyPinFromUrl || arrivedViaJoinLink) && !autoJoinFailed) {
    return (
      <div className="landing-page">
        <div className="landing-content join-lobby-content">
          <div className="quiz-loader-container">
            <div className="quiz-loader"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <div className="landing-content join-lobby-content">
        <h1 className="landing-hunt-title" style={{ color: headingColor }}>
          Join the Hunt
        </h1>
        <p className="landing-hunt-sub" style={{ color: textColor }}>
          Enter the lobby PIN your host shared to begin.
        </p>

        <form className="join-lobby-form" onSubmit={handleJoinLobby}>
          <input
            className="join-lobby-input"
            value={joinPin}
            onChange={(e) => setJoinPin(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
            placeholder="Lobby PIN"
            inputMode="numeric"
            autoFocus
          />
          {joinError && <div className="join-lobby-error">{joinError}</div>}
          <button
            type="submit"
            className="landing-play-btn"
            style={{ background: buttonBg, color: buttonText }}
            disabled={joinLoading}
          >
            {joinLoading ? "JOINING..." : "JOIN"}
          </button>
        </form>

        <div className="landing-footer">Powered by ExtraMile Play</div>
      </div>
    </div>
  );
};

export default JoinLobby;
